import { supabase } from '@/integrations/supabase/client';
import { sigmaToPercentile, getScoreValue } from '@/utils/scoreCalculations';
import { getProfileType } from '@/utils/profileTypeMapping';
import { DCTOS_COLORS } from '@/lib/dctos-config';
import { createLogger } from '@/utils/logger';

const logger = createLogger('ProfileAverages');

export interface ProfileAverages {
  [profileName: string]: {
    name: string;
    fullName: string;
    value: number;
    color: string;
    sampleSize: number;
    contributors: string[];
  }[];
}

// Map legacy profile names to current names
const LEGACY_NAME_MAPPING: Record<string, string> = {
  'Facilitator': 'Steady',
  'Influencer': 'Spark',
  'Visionary Leader': 'Luminary',
  'Specialist': 'Catalyst',
  'Team Builder': 'Connector',
};

export class ProfileAveragesService {
  private static cache: Map<string, ProfileAverages> = new Map();
  private static lastFetch: Map<string, number> = new Map();
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static async getProfileAverages(companyId?: string): Promise<ProfileAverages> {
    const cacheKey = companyId || 'global';
    const now = Date.now();
    
    // Return cached data if still valid
    const lastFetchTime = this.lastFetch.get(cacheKey) || 0;
    const cachedData = this.cache.get(cacheKey);
    if (cachedData && (now - lastFetchTime) < this.CACHE_DURATION) {
      return cachedData;
    }

    try {
      let query = supabase
        .from('survey_submissions')
        .select('profile_name, disc_code, scores, full_name')
        .not('profile_name', 'is', null)
        .not('scores', 'is', null)
        .not('full_name', 'is', null);
      
      // Add company filter if provided
      if (companyId) {
        query = query.eq('company_id', companyId);
      }
      
      const { data: submissions, error } = await query;

      if (error) {
        logger.error('Error fetching profile averages:', error);
        return {};
      }

      const profileAverages: ProfileAverages = {};

  // Group submissions by profile name and calculate averages
  const profileGroups: { [key: string]: { scores: any; fullName: string }[] } = {};
  
  submissions?.forEach(submission => {
    // Use disc_code as the grouping key, fall back to profile_name for legacy data
    const groupKey = submission.disc_code || submission.profile_name;
    if (!groupKey) return; // Skip if neither exists
    
    if (!profileGroups[groupKey]) {
      profileGroups[groupKey] = [];
    }
    profileGroups[groupKey].push({
      scores: submission.scores,
      fullName: submission.full_name
    });
  });

  logger.debug('Profile groups from database (by disc_code):', Object.keys(profileGroups));

  // Calculate averages for each profile
  Object.entries(profileGroups).forEach(([discCode, submissions]) => {
    const scores = submissions.map(s => s.scores);
    const contributors = submissions.map(s => s.fullName);
    const averages = this.calculateAverageScores(scores);
    const sampleSize = scores.length;
    
    // Map disc_code to profile type for display
    const profileType = getProfileType(discCode);
    const displayName = profileType.type;
    
    logger.debug(`Processing disc_code "${discCode}" -> profile "${displayName}"`);
    
    // Store by display name for UI compatibility
    profileAverages[displayName] = this.formatScoresForChart(averages, sampleSize, contributors);
  });

      logger.debug('Final profile averages keys:', Object.keys(profileAverages));

      this.cache.set(cacheKey, profileAverages);
      this.lastFetch.set(cacheKey, now);
      
      return profileAverages;
    } catch (error) {
      logger.error('Error in getProfileAverages:', error);
      return {};
    }
  }

  private static normalizeProfileName(profileName: string): string {
    // Remove "The " prefix if it exists to match mapping format
    let normalized = profileName.startsWith('The ') ? profileName.substring(4) : profileName;
    
    // Convert legacy names to current names
    normalized = LEGACY_NAME_MAPPING[normalized] || normalized;
    
    return normalized;
  }

  private static calculateAverageScores(scoresArray: any[]): {
    dominance: number;
    extraversion: number;
    patience: number;
    formality: number;
  } {
    const totals = {
      dominance: 0,
      extraversion: 0,
      patience: 0,
      formality: 0,
      count: 0
    };

    scoresArray.forEach(scores => {
      let dominance = 0, extraversion = 0, patience = 0, formality = 0;
      
      // For dual assessments, use workBehavior scores (since disc_code is based on work context)
      if (scores?.workBehavior?.scores) {
        const workScores = scores.workBehavior.scores;
        dominance = workScores.dominance ?? 0;
        extraversion = workScores.extraversion ?? 0;
        patience = workScores.patience ?? 0;
        formality = workScores.formality ?? 0;
        logger.debug('Using workBehavior scores:', { dominance, extraversion, patience, formality });
      } else {
        // Fallback to existing logic for single assessments
        dominance = getScoreValue(scores, 'dominance') || getScoreValue(scores, 'A') || 0;
        extraversion = getScoreValue(scores, 'extraversion') || getScoreValue(scores, 'B') || 0;
        patience = getScoreValue(scores, 'patience') || getScoreValue(scores, 'C') || 0;
        formality = getScoreValue(scores, 'formality') || getScoreValue(scores, 'D') || 0;
        logger.debug('Using single assessment scores:', { dominance, extraversion, patience, formality });
      }

      totals.dominance += dominance;
      totals.extraversion += extraversion;
      totals.patience += patience;
      totals.formality += formality;
      totals.count++;
    });

    return {
      dominance: totals.count > 0 ? totals.dominance / totals.count : 0,
      extraversion: totals.count > 0 ? totals.extraversion / totals.count : 0,
      patience: totals.count > 0 ? totals.patience / totals.count : 0,
      formality: totals.count > 0 ? totals.formality / totals.count : 0
    };
  }

  private static formatScoresForChart(averages: {
    dominance: number;
    extraversion: number;
    patience: number;
    formality: number;
  }, sampleSize: number, contributors: string[]) {
    return [
      {
        name: 'D',
        fullName: 'Drive',
        value: sigmaToPercentile(averages.dominance),
        color: DCTOS_COLORS.drive,
        sampleSize,
        contributors
      },
      {
        name: 'C',
        fullName: 'Connection',
        value: sigmaToPercentile(averages.extraversion),
        color: DCTOS_COLORS.connection,
        sampleSize,
        contributors
      },
      {
        name: 'T',
        fullName: 'Task',
        value: sigmaToPercentile(averages.patience),
        color: DCTOS_COLORS.task,
        sampleSize,
        contributors
      },
      {
        name: 'O',
        fullName: 'Order',
        value: sigmaToPercentile(averages.formality),
        color: DCTOS_COLORS.order,
        sampleSize,
        contributors
      }
    ];
  }
}
